import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ArrowRight, Sparkles, Clock, Loader2, Briefcase } from 'lucide-react';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import {
  fetchProposalCategories,
  buildProposalCategoryCards,
  getCategoryIcon,
  getCategoryColors,
  routeForCategory,
  PROPOSAL_CATEGORY_ROUTES,
} from '../lib/proposalCategories';

/**
 * Proposal Category Selection — 3-card picker (Business Plan & Funding,
 * Grant Proposal, Service Proposal). Live categories merge API metadata;
 * routes and category_id values for available cards are unchanged.
 */
export default function ProposalCategoryPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProposalCategories();
        if (!cancelled) setCategories(buildProposalCategoryCards(list));
      } catch (e) {
        if (!cancelled) setError('Could not load proposal categories.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = (cat) => {
    const route = routeForCategory(cat);
    if (route) navigate(route);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="proposal-category-page">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-2" data-testid="back-to-dashboard">
            <DocManiaLogoImage size={28} />
          </button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-8 max-w-2xl">
          <Badge variant="secondary" className="mb-3">
            <Sparkles className="mr-1 h-3 w-3" /> Business Proposal Builder
          </Badge>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
            What kind of proposal are you creating?
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Pick a category — we&apos;ll only show you templates and form fields relevant to that proposal type.
          </p>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground" data-testid="category-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading categories…
          </div>
        )}

        {error && !loading && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive" data-testid="category-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat) => {
              const Icon = getCategoryIcon(cat.icon);
              const colors = getCategoryColors(cat.color);
              const isBusinessPlan = cat.category_id === 'business_plan_funding';
              const comingSoon = !isBusinessPlan && (cat.coming_soon_only || !cat.is_available);
              const creditLabel = isBusinessPlan ? 20 : cat.credit_cost;
              return (
                <Card
                  key={cat.category_id}
                  className={[
                    'group transition-all',
                    comingSoon
                      ? 'cursor-default opacity-60 grayscale-[0.15]'
                      : 'cursor-pointer hover:border-primary hover:shadow-md',
                  ].join(' ')}
                  onClick={
                    isBusinessPlan
                      ? () => navigate('/documents/business-proposal/templates?category=business_plan_funding')
                      : comingSoon
                        ? undefined
                        : () => handleSelect(cat)
                  }
                  data-testid={`category-card-${cat.category_id}`}
                >
                  <CardContent className="p-6">
                    <div className="mb-3 flex items-start justify-between">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colors.bg} ring-1 ${colors.ring}`}>
                        <Icon className={`h-6 w-6 ${colors.text}`} />
                      </div>
                      {comingSoon
                        ? <Badge variant="outline" className="text-amber-700"><Clock className="mr-1 h-3 w-3" /> Coming Soon</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Available · {creditLabel} cr</Badge>}
                    </div>
                    <h3 className="text-base font-semibold leading-tight text-foreground">{cat.display_name}</h3>
                    <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{cat.description}</p>
                    {!comingSoon && (
                      <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                        Start →
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Coming-Soon placeholder for categories that aren't shipped yet.
 * Looks up the category by ``?category=`` query param so the heading,
 * icon and accent color match the picked category. Routes the user to
 * Business Grant Proposal as the fallback CTA.
 */
export function ProposalCategoryComingSoonPage() {
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);

  const slug = new URLSearchParams(window.location.search).get('category') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProposalCategories();
        if (cancelled) return;
        const match = list.find((c) => c.category_id === slug);
        setCat(match || null);
      } catch (e) {
        // ignore — render generic placeholder
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const Icon = cat ? getCategoryIcon(cat.icon) : Briefcase;
  const colors = cat ? getCategoryColors(cat.color) : getCategoryColors('emerald');

  return (
    <div className="min-h-screen bg-background" data-testid="proposal-coming-soon-page">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
            <DocManiaLogoImage size={28} />
          </button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/services/business-proposal-builder')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> All categories
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16">
        <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${colors.bg} ring-1 ${colors.ring}`}>
          <Icon className={`h-8 w-8 ${colors.text}`} />
        </div>
        <h1 className="text-center text-3xl font-extrabold tracking-tight text-foreground">
          {loading ? 'Loading…' : (cat?.display_name || 'This proposal category')} is being prepared
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-center text-base text-muted-foreground">
          We&apos;re finalising the templates, form fields and content suggestions for{' '}
          <strong className="text-foreground">{cat?.display_name?.toLowerCase() || 'this category'}</strong>.
          It&apos;ll be available very shortly. In the meantime, you can create a{' '}
          <strong className="text-foreground">Business Grant Proposal</strong> today.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button
            onClick={() => navigate(PROPOSAL_CATEGORY_ROUTES.business_grant_proposal)}
            data-testid="coming-soon-use-grant-instead"
          >
            Use Business Grant Proposal <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/services/business-proposal-builder')} data-testid="coming-soon-back-categories">
            Back to categories
          </Button>
        </div>
      </main>
    </div>
  );
}
